import type { OpenCodeEvent, SessionActiveOutput, SessionMessageInfo } from "@opencode-ai/client/promise"
import { ExecutionBindingError } from "@/cowork/execution-context"
import { coworkUserPrompt } from "@/cowork/user-prompt"

type RecordValue = Record<string, unknown>
const record = (value: unknown): RecordValue => value && typeof value === "object" && !Array.isArray(value) ? value as RecordValue : {}
const millis = (value: unknown) => typeof value === "string" ? Date.parse(value) : value
const time = (value: unknown) => Object.fromEntries(Object.entries(record(value)).map(([key, value]) => [key, millis(value)]))

export function nativeSessionActive(value: unknown): SessionActiveOutput {
  if (Array.isArray(value)) return Object.fromEntries(value.map((item) => {
    const entry = record(item)
    if (typeof entry.sessionID !== "string" || !entry.sessionID) throw new ExecutionBindingError()
    return [entry.sessionID, { type: "running" as const }]
  }))
  if (!value || typeof value !== "object" || Object.values(value).some((entry) => record(entry).type !== "running"))
    throw new ExecutionBindingError()
  return value as SessionActiveOutput
}

// The pinned runtime retains content IDs and structured tool metadata; the UI
// SDK's newer projection uses ordinals and `metadata`. Preserve IDs as extra
// fields so live deltas and reloaded snapshots address the same content.
export function nativeSessionMessage(value: unknown): SessionMessageInfo {
  const message = record(value)
  if (typeof message.id !== "string" || typeof message.type !== "string" || !message.time) throw new ExecutionBindingError()
  const result = { ...message, time: time(message.time) }
  if (message.type === "assistant" && Array.isArray(message.content)) {
    return { ...result, content: message.content.map((value) => {
      const item = record(value)
      if (item.type === "reasoning") return { ...item, time: item.time ? time(item.time) : undefined, state: item.state ?? item.providerMetadata }
      if (item.type !== "tool") return item
      const state = record(item.state)
      const provider = record(item.provider)
      return { ...item, time: time(item.time), executed: item.executed ?? provider.executed,
        providerState: item.providerState ?? provider.metadata, providerResultState: item.providerResultState ?? provider.resultMetadata,
        state: { ...state, status: state.status === "pending" ? "streaming" : state.status,
          ...(state.status === "pending" || state.status === "streaming" ? {} : { metadata: state.metadata ?? state.structured ?? {} }),
        },
      }
    }) } as unknown as SessionMessageInfo
  }
  if (message.type === "user") return { ...result,
    text: typeof message.text === "string" ? coworkUserPrompt(message.text) : message.text,
    files: Array.isArray(message.files) ? message.files.map((value) => {
      const file = record(value)
      const source = record(file.source)
      return { ...file,
        source: source.type === "uri" || source.type === "inline" ? file.source : { type: "inline" },
        mention: file.mention ?? (typeof source.text === "string" ? file.source : undefined),
      }
    }) : undefined,
    agents: Array.isArray(message.agents) ? message.agents.map((value) => {
      const agent = record(value)
      return { ...agent, mention: agent.mention ?? agent.source }
    }) : undefined,
  } as unknown as SessionMessageInfo
  if (message.type === "compaction" && !message.status) return { ...result, status: "completed" } as unknown as SessionMessageInfo
  return result as unknown as SessionMessageInfo
}

export function nativeSessionEvent(event: OpenCodeEvent, source: readonly SessionMessageInfo[]) {
  const wire = event as unknown as { type: string; id: string; metadata?: unknown; data: RecordValue }
  if (!wire.type.startsWith("session.next.")) return
  const data = wire.data
  const created = millis(data.timestamp)
  const make = (type: string, data: RecordValue) => ({ ...wire, type, created, data }) as unknown as OpenCodeEvent
  const type = wire.type.replace("session.next.", "session.")
  if (type === "session.prompt.admitted") return { event: make("session.input.admitted", {
    sessionID: data.sessionID, inputID: data.messageID,
    input: { id: data.messageID, type: "user", data: data.prompt, delivery: data.delivery },
  }) }
  if (type === "session.prompted") return { event: make("session.input.promoted", { sessionID: data.sessionID, inputID: data.messageID }) }
  if (type === "session.agent.switched") return { event: make("session.agent.selected", data) }
  if (type === "session.model.switched") return { event: make("session.model.selected", data) }
  if (type === "session.retried") {
    const assistant = source.findLast((item) => item.type === "assistant")
    if (!assistant) return { ignored: true }
    return { event: make("session.retry.scheduled", { ...data, assistantMessageID: assistant.id, at: created }) }
  }
  if (type.startsWith("session.text.") || type.startsWith("session.reasoning.")) {
    const kind = type.startsWith("session.text.") ? "text" : "reasoning"
    const id = kind === "text" ? data.textID : data.reasoningID
    const assistant = source.find((item) => item.id === data.assistantMessageID)
    const content = assistant?.type === "assistant" ? assistant.content.filter((item) => item.type === kind) : []
    const found = content.findIndex((item) => record(item).id === id)
    // A late delta without its start cannot establish a content ordinal. The
    // caller reloads the actual message rather than appending to a guessed part.
    if (found < 0 && !type.endsWith(".started")) return { missing: data.assistantMessageID as string }
    return { event: make(type, { ...data, ordinal: found < 0 ? content.length : found, state: data.providerMetadata }),
      content: { messageID: data.assistantMessageID, kind, id, ordinal: found < 0 ? content.length : found } }
  }
  if (type.startsWith("session.tool.")) {
    const provider = record(data.provider)
    return { event: make(type, { ...data, executed: provider.executed, state: provider.metadata,
      resultState: provider.metadata, metadata: data.structured ?? {}, content: data.content ?? [],
    }) }
  }
  if (type.startsWith("session.compaction.")) return { event: make(type, { ...data, inputID: data.messageID }) }
  if (type.startsWith("session.step.") || type.startsWith("session.revert.")) return { event: make(type, data) }
  return { ignored: true }
}
